import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiBlobUrl, apiFetch } from '../../lib/api';
import { useChatAssistenza } from '../../context/ChatAssistenzaContext';
import { useSocieta } from '../../context/SocietaContext';
import { ChatEsitoTipo, ChatMessaggio, TicketProposto } from '../../pages/assistenza/ticketAssistenza';
import FormTicket, { FormTicketValori, formTicketIniziale, immaginiDaIncolla, inviaTicket, validaFormTicket } from './FormTicket';
import ModalNuovoTicket from './ModalNuovoTicket';

// Pannello laterale dell'assistente. Montato una volta in App.tsx, fuori
// dalle route: sopravvive alla navigazione. Fasi:
//   chat            → conversazione
//   proposta        → form ticket precompilato dall'assistente
//   non_disponibile → l'AI ha fallito: pulsante per il modulo classico
//   classica        → modale classica (AI non configurata o scelta dell'utente)
// Gli screenshot incollati (Ctrl+V) viaggiano col messaggio, li vede il
// modello e, se nasce un ticket, diventano suoi allegati.

type Fase = 'chat' | 'proposta' | 'non_disponibile' | 'classica';

const CHIAVE_SESSIONE = 'bop_chat_assistenza_id';
const MAX_IMMAGINI = 4;

interface ImmagineLocale { url: string; nome: string; file?: File }
interface MessaggioLocale { chiave: string; ruolo: 'UTENTE' | 'ASSISTENTE'; testo: string; fonti?: string[]; immagini?: ImmagineLocale[] }

interface ChatRipristinata {
  id: number;
  stato: string;
  urlPagina: string | null;
  modulo: string | null;
  moduloLabel: string | null;
  messaggioApertura: string;
  messaggi: ChatMessaggio[];
}

function leggiSessione(): number | null {
  try {
    const v = sessionStorage.getItem(CHIAVE_SESSIONE);
    return v ? Number(v) : null;
  } catch (_) { return null; }
}
function scriviSessione(id: number | null) {
  try {
    if (id) sessionStorage.setItem(CHIAVE_SESSIONE, String(id));
    else sessionStorage.removeItem(CHIAVE_SESSIONE);
  } catch (_) {}
}

function trascrizioneUtente(messaggi: MessaggioLocale[]): string {
  return messaggi.filter(m => m.ruolo === 'UTENTE').map(m => m.testo).join('\n');
}
function fileUtente(messaggi: MessaggioLocale[]): File[] {
  return messaggi.flatMap(m => (m.immagini || []).map(i => i.file).filter((f): f is File => !!f));
}

export default function ChatAssistenza() {
  const { aperta, contesto, chatIdRipristino, apriEsistente, chiudi } = useChatAssistenza();
  const { societaAttiva } = useSocieta();
  const navigate = useNavigate();

  const [chatId, setChatId] = useState<number | null>(null);
  const [pagina, setPagina] = useState<{ urlPagina?: string; moduloLabel?: string }>({});
  const [messaggi, setMessaggi] = useState<MessaggioLocale[]>([]);
  const [input, setInput] = useState('');
  const [inAttesa, setInAttesa] = useState<ImmagineLocale[]>([]);
  const [attesa, setAttesa] = useState(false);
  const [fase, setFase] = useState<Fase>('chat');
  const [errore, setErrore] = useState('');
  const [ultimoEsito, setUltimoEsito] = useState<ChatEsitoTipo | null>(null);
  const [proposta, setProposta] = useState<FormTicketValori | null>(null);
  const [erroreProposta, setErroreProposta] = useState('');
  const [inviando, setInviando] = useState(false);
  const listaRef = useRef<HTMLDivElement>(null);

  const azzera = useCallback(() => {
    setChatId(null); setPagina({}); setMessaggi([]); setInput(''); setInAttesa([]); setAttesa(false); setFase('chat');
    setErrore(''); setUltimoEsito(null); setProposta(null); setErroreProposta('');
    scriviSessione(null);
  }, []);

  const daProposta = useCallback((t: TicketProposto, p: { urlPagina?: string; moduloLabel?: string }): FormTicketValori => ({
    ...formTicketIniziale,
    tipo: t.tipo, priorita: t.priorita, titolo: t.titolo, descrizione: t.descrizione,
    modulo: t.modulo ?? p.moduloLabel ?? '',
    urlPagina: p.urlPagina ?? '',
  }), []);

  // Ripristino dopo refresh: se in sessione c'è una chat, la riapre.
  useEffect(() => {
    const id = leggiSessione();
    if (id && !aperta) apriEsistente(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apertura: crea la chat, oppure ricarica quella da ripristinare.
  useEffect(() => {
    if (!aperta) return;
    let annullato = false;
    (async () => {
      azzera();
      setAttesa(true);
      try {
        if (chatIdRipristino) {
          // @api GET /api/assistenza/chat/:id -> { success: true, data: { id, stato, urlPagina, modulo, moduloLabel, societaId, ticketId, messaggioApertura, messaggi: [{ id, ruolo, testo, esito, allegati: [{ indice, nomeFile, mimeType, dimensione }], createdAt }] } }
          const res = await apiFetch<{ success: boolean; data: ChatRipristinata }>(`/api/assistenza/chat/${chatIdRipristino}`);
          if (annullato) return;
          const c = res.data;
          if (c.stato !== 'APERTA') { scriviSessione(null); chiudi(); return; }
          const p = { urlPagina: c.urlPagina ?? undefined, moduloLabel: c.moduloLabel ?? undefined };
          setChatId(c.id);
          setPagina(p);
          const locali: MessaggioLocale[] = [{ chiave: 'apertura', ruolo: 'ASSISTENTE', testo: c.messaggioApertura }];
          for (const m of c.messaggi) {
            const immagini: ImmagineLocale[] = [];
            for (const a of m.allegati || []) {
              try {
                // @api GET /api/assistenza/chat/:id/immagini/:msgId/:indice -> (immagine)
                immagini.push({ url: await apiBlobUrl(`/api/assistenza/chat/${c.id}/immagini/${m.id}/${a.indice}`), nome: a.nomeFile });
              } catch (_) {}
            }
            locali.push({ chiave: String(m.id), ruolo: m.ruolo, testo: m.testo, fonti: m.esito?.fonti, immagini });
          }
          setMessaggi(locali);
          const ultimo = c.messaggi[c.messaggi.length - 1];
          setUltimoEsito(ultimo?.esito?.esito ?? null);
          if (ultimo?.esito?.esito === 'PROPOSTA_TICKET' && ultimo.esito.ticket) {
            setProposta(daProposta(ultimo.esito.ticket, p));
            setFase('proposta');
          }
          scriviSessione(c.id);
          return;
        }

        const p = { urlPagina: contesto?.urlPagina, moduloLabel: contesto?.moduloLabel };
        setPagina(p);
        // @api POST /api/assistenza/chat { societaId, urlPagina?, modulo?, moduloLabel? } -> { success: true, data: { id, messaggioApertura } }
        const res = await apiFetch<{ success: boolean; data: { id: number; messaggioApertura: string } }>('/api/assistenza/chat', {
          method: 'POST',
          body: JSON.stringify({ societaId: societaAttiva?.id ?? null, ...(contesto ?? {}) }),
        });
        if (annullato) return;
        setChatId(res.data.id);
        setMessaggi([{ chiave: 'apertura', ruolo: 'ASSISTENTE', testo: res.data.messaggioApertura }]);
        scriviSessione(res.data.id);
      } catch (e: any) {
        if (annullato) return;
        if (chatIdRipristino) { scriviSessione(null); chiudi(); return; }
        if (e?.status === 409 || e?.message === 'AI_NON_CONFIGURATA') setFase('classica');
        else setErrore(e?.message || 'Errore nell\'apertura della chat');
      } finally {
        if (!annullato) setAttesa(false);
      }
    })();
    return () => { annullato = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aperta, contesto, chatIdRipristino]);

  useEffect(() => {
    if (listaRef.current) listaRef.current.scrollTop = listaRef.current.scrollHeight;
  }, [messaggi, attesa, fase]);

  // Screenshot incollato nel campo di testo → in attesa di invio col prossimo messaggio
  const onPaste = (e: React.ClipboardEvent) => {
    const nuove = immaginiDaIncolla(e, inAttesa.length);
    if (nuove.length === 0) return;
    e.preventDefault();
    setInAttesa(prev => [...prev, ...nuove.map(f => ({ url: URL.createObjectURL(f), nome: f.name, file: f }))].slice(0, MAX_IMMAGINI));
  };
  const rimuoviInAttesa = (i: number) => setInAttesa(prev => prev.filter((_, k) => k !== i));

  const invia = async (testo: string, forzaProposta = false) => {
    if (!chatId || attesa) return;
    const t = testo.trim();
    const immagini = forzaProposta ? [] : inAttesa;
    if (!t && !forzaProposta && immagini.length === 0) return;
    setErrore('');
    if (t || immagini.length) setMessaggi(m => [...m, { chiave: `u-${Date.now()}`, ruolo: 'UTENTE', testo: t || 'Ecco uno screenshot di quello che vedo.', immagini }]);
    setInput('');
    if (!forzaProposta) setInAttesa([]);
    setAttesa(true);
    try {
      // @api POST /api/assistenza/chat/:id/messaggi { testo?, forzaProposta? } | FormData{ testo?, forzaProposta?, immagini[]? } -> { success: true, data: { messaggio: { id, ruolo, testo, esito, allegati, createdAt } } }
      let body: BodyInit;
      if (immagini.length) {
        const fd = new FormData();
        fd.append('testo', t);
        fd.append('forzaProposta', String(forzaProposta));
        immagini.forEach(i => { if (i.file) fd.append('immagini', i.file, i.nome); });
        body = fd;
      } else {
        body = JSON.stringify({ testo: t, forzaProposta });
      }
      const res = await apiFetch<{ success: boolean; data: { messaggio: ChatMessaggio } }>(`/api/assistenza/chat/${chatId}/messaggi`, { method: 'POST', body });
      const m = res.data.messaggio;
      setMessaggi(prev => [...prev, { chiave: String(m.id), ruolo: 'ASSISTENTE', testo: m.testo, fonti: m.esito?.fonti }]);
      setUltimoEsito(m.esito?.esito ?? null);
      if (m.esito?.esito === 'PROPOSTA_TICKET' && m.esito.ticket) {
        setProposta(daProposta(m.esito.ticket, pagina));
        setFase('proposta');
      }
    } catch (e: any) {
      if (e?.status === 503 || e?.message === 'ASSISTENTE_NON_DISPONIBILE') {
        // Il backend ha scartato il messaggio dal DB, ma qui resta (testo e
        // screenshot): serve a precompilare il ticket nella modale classica.
        setFase('non_disponibile');
      } else {
        setErrore(e?.message || 'Errore nell\'invio');
      }
    } finally {
      setAttesa(false);
    }
  };

  const chiudiChat = async (stato: 'RISOLTA' | 'ABBANDONATA') => {
    if (chatId) {
      try {
        // @api POST /api/assistenza/chat/:id/chiudi { stato } -> { success: true }
        await apiFetch(`/api/assistenza/chat/${chatId}/chiudi`, { method: 'POST', body: JSON.stringify({ stato }) });
      } catch (_) {}
    }
    azzera();
    chiudi();
  };

  const vaiAlTicket = (id: number) => {
    azzera();
    chiudi();
    navigate(`/assistenza/richieste/${id}`);
  };

  const apriTicketProposto = async () => {
    if (!proposta) return;
    const msg = validaFormTicket(proposta);
    if (msg) { setErroreProposta(msg); return; }
    setInviando(true); setErroreProposta('');
    try {
      // Gli screenshot già inviati in chat li copia il backend fra gli allegati del ticket
      const id = await inviaTicket(proposta, societaAttiva?.id ?? null, chatId);
      vaiAlTicket(id);
    } catch (e: any) {
      setErroreProposta(e.message || 'Errore nell\'invio della richiesta');
    } finally {
      setInviando(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); invia(input); }
  };

  if (!aperta) return null;

  if (fase === 'classica') {
    return (
      <ModalNuovoTicket
        iniziale={{
          urlPagina: pagina.urlPagina ?? '',
          modulo: pagina.moduloLabel ?? '',
          descrizione: trascrizioneUtente(messaggi),
          files: fileUtente(messaggi),
        }}
        chatId={chatId}
        onClose={() => chiudiChat('ABBANDONATA')}
        onCreato={vaiAlTicket}
      />
    );
  }

  const puoiInviare = !attesa && !!chatId && (!!input.trim() || inAttesa.length > 0);

  return (
    <aside className="bop-chat-assistenza" role="dialog" aria-label="Assistente">
      <div className="bop-chat-assistenza__testata">
        <div>
          <h3 className="bop-chat-assistenza__titolo">Assistente BOP</h3>
          <div className="bop-chat-assistenza__contesto">
            {pagina.moduloLabel ? `Pagina: ${pagina.moduloLabel}` : 'Nessuna pagina di riferimento'}
            {societaAttiva ? ` · ${societaAttiva.nome}` : ''}
          </div>
        </div>
        <button className="bop-chat-assistenza__chiudi" aria-label="Chiudi" onClick={() => chiudiChat('ABBANDONATA')}>×</button>
      </div>

      {fase === 'proposta' && proposta ? (
        <>
          <div className="bop-chat-assistenza__proposta">
            <p className="bop-chat-assistenza__proposta-intro">
              Ecco il ticket che ho preparato. Correggi quello che vuoi e conferma.
              {messaggi.some(m => m.immagini && m.immagini.length > 0) && ' Gli screenshot incollati in chat saranno allegati al ticket.'}
            </p>
            {erroreProposta && <div className="bop-inline-error">{erroreProposta}</div>}
            <FormTicket valori={proposta} onChange={setProposta} />
          </div>
          <div className="bop-chat-assistenza__azioni">
            <button className="bop-btn bop-btn-secondary" onClick={() => setFase('chat')} disabled={inviando}>Torna alla chat</button>
            <button className="bop-btn bop-btn-primary" onClick={apriTicketProposto} disabled={inviando}>{inviando ? 'Invio…' : 'Apri il ticket'}</button>
          </div>
        </>
      ) : (
        <>
          <div className="bop-chat-assistenza__lista" ref={listaRef}>
            {messaggi.map(m => (
              <div key={m.chiave} className={`bop-chat-assistenza__bolla bop-chat-assistenza__bolla--${m.ruolo === 'UTENTE' ? 'utente' : 'assistente'}`}>
                {m.testo}
                {m.immagini && m.immagini.length > 0 && (
                  <div className="bop-chat-assistenza__immagini">
                    {m.immagini.map((img, i) => (
                      <a key={i} href={img.url} target="_blank" rel="noopener noreferrer">
                        <img className="bop-chat-assistenza__immagine" src={img.url} alt={img.nome} />
                      </a>
                    ))}
                  </div>
                )}
                {m.fonti && m.fonti.length > 0 && (
                  <div className="bop-chat-assistenza__fonti">
                    {m.fonti.map(f => (
                      <a key={f} className="bop-chat-assistenza__fonte" href={`/assistenza/knowledge-base?doc=${encodeURIComponent(f)}`} target="_blank" rel="noopener noreferrer">📄 {f}</a>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {attesa && <div className="bop-chat-assistenza__attesa">L'assistente sta scrivendo…</div>}
            {fase === 'non_disponibile' && (
              <div className="bop-chat-assistenza__avviso">
                L'assistente non è disponibile in questo momento. Puoi aprire il ticket direttamente: quello che hai scritto e gli screenshot vengono riportati nel ticket.
              </div>
            )}
            {errore && <div className="bop-inline-error">{errore}</div>}
          </div>

          <div className="bop-chat-assistenza__azioni">
            {fase === 'non_disponibile' ? (
              <button className="bop-btn bop-btn-primary" onClick={() => setFase('classica')}>Apri il ticket direttamente</button>
            ) : (
              <>
                {ultimoEsito === 'RISPOSTA' && (
                  <button className="bop-btn bop-btn-secondary" onClick={() => chiudiChat('RISOLTA')} disabled={attesa}>Risolto, grazie</button>
                )}
                {ultimoEsito === 'PROPOSTA_TICKET' && proposta && (
                  <button className="bop-btn bop-btn-secondary" onClick={() => setFase('proposta')} disabled={attesa}>Vedi la proposta</button>
                )}
                <button className="bop-btn bop-btn-ghost" onClick={() => invia('', true)} disabled={attesa || !chatId}>Apri comunque il ticket</button>
              </>
            )}
          </div>

          {fase !== 'non_disponibile' && (
            <>
              {inAttesa.length > 0 && (
                <div className="bop-chat-assistenza__in-attesa">
                  {inAttesa.map((img, i) => (
                    <div key={i} className="bop-chat-assistenza__anteprima">
                      <img src={img.url} alt={img.nome} />
                      <button type="button" className="bop-chat-assistenza__anteprima-rimuovi" aria-label={`Rimuovi ${img.nome}`} onClick={() => rimuoviInAttesa(i)}>×</button>
                    </div>
                  ))}
                </div>
              )}
              <div className="bop-chat-assistenza__campo">
                <textarea
                  className="bop-input"
                  rows={2}
                  placeholder="Scrivi qui… (Invio per inviare, Ctrl+V per incollare uno screenshot)"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={onKeyDown}
                  onPaste={onPaste}
                  disabled={attesa || !chatId}
                  maxLength={2000}
                />
                <button className="bop-btn bop-btn-primary" onClick={() => invia(input)} disabled={!puoiInviare}>Invia</button>
              </div>
            </>
          )}
        </>
      )}
    </aside>
  );
}
